import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { SingleSelect } from "./MultiSelect";
import { api } from "../../lib/api";
import { Plus, X, RotateCcw, Save, ExternalLink } from "lucide-react";

const TIERS = ["Low", "Moderate", "Elevated", "High", "Severe"];

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="label-eyebrow mb-2">{label}</div>
    {children}
  </div>
);

const TextInput = ({ value, onChange, testId, placeholder }) => (
  <input
    className="aurora-input"
    value={value || ""}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
  />
);

const TextArea = ({ value, onChange, testId, rows = 3, placeholder }) => (
  <textarea
    className="aurora-input"
    rows={rows}
    value={value || ""}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
  />
);

const NumberInput = ({ value, onChange, testId, min = 0, max = 10, step = 0.1 }) => (
  <input
    type="number"
    className="aurora-input"
    min={min}
    max={max}
    step={step}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    data-testid={testId}
  />
);

const StringList = ({ items, onChange, testIdPrefix, placeholder }) => (
  <div className="space-y-2">
    {(items || []).map((it, i) => (
      <div key={i} className="flex gap-2 items-start">
        <textarea
          className="aurora-input flex-1"
          rows={2}
          value={it || ""}
          placeholder={placeholder}
          onChange={(e) => {
            const next = [...items];
            next[i] = e.target.value;
            onChange(next);
          }}
          data-testid={`${testIdPrefix}-${i}`}
        />
        <button
          type="button"
          onClick={() => onChange(items.filter((_, j) => j !== i))}
          className="btn-ghost shrink-0 px-2 py-2"
          data-testid={`${testIdPrefix}-remove-${i}`}
          aria-label="Remove"
        >
          <X size={14} />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange([...(items || []), ""])}
      className="btn-ghost inline-flex items-center text-xs"
      data-testid={`${testIdPrefix}-add`}
    >
      <Plus size={12} className="mr-1" /> Add
    </button>
  </div>
);

export default function InlineResultEditor({ sub, onChange, resultUrl }) {
  // Local working copy — only persisted to backend when Save Edits is clicked
  const [draft, setDraft] = useState(sub.fullResult);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    setDraft(sub.fullResult);
  }, [sub.fullResult]);

  if (!draft) return null;

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const patchSummary = (p) =>
    setDraft((d) => ({ ...d, executiveSummary: { ...(d.executiveSummary || {}), ...p } }));
  const patchRec = (p) =>
    setDraft((d) => ({ ...d, auroraRecommendation: { ...(d.auroraRecommendation || {}), ...p } }));

  const patchDriver = (idx, val) => {
    const drivers = [...(draft.topRiskDrivers || [])];
    drivers[idx] = { ...drivers[idx], riskDriver: val };
    patch({ topRiskDrivers: drivers });
  };

  const patchRiskScore = (idx, p) => {
    const list = [...(draft.riskScores || [])];
    list[idx] = { ...list[idx], ...p };
    patch({ riskScores: list });
  };

  const patchWatch = (idx, p) => {
    const list = [...(draft.whatToWatch || [])];
    list[idx] = { ...list[idx], ...p };
    patch({ whatToWatch: list });
  };
  const addWatch = () =>
    patch({ whatToWatch: [...(draft.whatToWatch || []), { development: "", relevance: "" }] });
  const removeWatch = (idx) =>
    patch({ whatToWatch: (draft.whatToWatch || []).filter((_, j) => j !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      await api.adminSaveResult(sub.submissionId, draft);
      toast.success("Edits saved");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save edits");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!window.confirm("Discard all edits and restore the last AI-generated version?")) return;
    setReverting(true);
    try {
      await api.adminRevertResult(sub.submissionId);
      toast.success("Reverted to AI version");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to revert");
    } finally {
      setReverting(false);
    }
  };

  const drivers = draft.topRiskDrivers || [{ rank: 1, riskDriver: "" }, { rank: 2, riskDriver: "" }, { rank: 3, riskDriver: "" }];

  return (
    <div className="border-2 border-[#A88059] p-8 mb-12 bg-[#FAF5EE]" data-testid="inline-editor">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#6B4D2B] mb-2">Inline Editor</div>
          <h3 className="font-heading-bold text-2xl text-[#190F08]">
            Refine the Client Assessment
          </h3>
          {sub.edited && (
            <span
              className="inline-block mt-3 px-3 py-1 text-[10px] font-heading uppercase tracking-[0.22em] border border-[#D49A36] text-[#D49A36]"
              data-testid="edited-badge-detail"
            >
              Edited
            </span>
          )}
        </div>
        <div className="flex gap-3 flex-wrap">
          {resultUrl && (
            <a
              href={resultUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost inline-flex items-center"
              data-testid="btn-open-result-from-editor"
            >
              <ExternalLink size={14} className="mr-1.5" /> Open Result
            </a>
          )}
          <button
            type="button"
            onClick={revert}
            disabled={reverting || saving}
            className="btn-ghost inline-flex items-center"
            data-testid="btn-revert-ai"
          >
            <RotateCcw size={14} className="mr-1.5" />
            {reverting ? "Reverting..." : "Revert to AI version"}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || reverting}
            className="btn-primary inline-flex items-center"
            data-testid="btn-save-edits-result"
          >
            <Save size={14} className="mr-1.5" />
            {saving ? "Saving..." : "Save Edits"}
          </button>
        </div>
      </div>

      <p className="font-body text-sm text-[#6B5E4A] mb-8 italic">
        Edits are saved to the client deliverable. The client&apos;s PDF is generated live from the
        edited content the next time they download — no separate regeneration needed.
      </p>

      {/* Overall */}
      <div className="grid md:grid-cols-[1fr_1fr_2fr] gap-5 mb-8">
        <Field label="Overall Score (0–10)">
          <NumberInput
            value={draft.overallScore}
            onChange={(v) => patch({ overallScore: v })}
            testId="edit-overall-score"
          />
        </Field>
        <Field label="Tier">
          <SingleSelect
            testId="edit-overall-tier"
            options={TIERS}
            value={draft.tier || ""}
            onChange={(v) => patch({ tier: v })}
          />
        </Field>
        <Field label="Tier Rationale">
          <TextInput
            value={draft.tierRationale}
            onChange={(v) => patch({ tierRationale: v })}
            testId="edit-tier-rationale"
          />
        </Field>
      </div>

      {/* Top 3 Risk Drivers */}
      <div className="mb-10">
        <div className="font-heading-bold text-lg text-[#190F08] mb-4">Top 3 Risk Drivers</div>
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3 items-start">
              <div className="font-heading text-xl tabular-nums text-[#A88059] w-8 pt-2">
                0{i + 1}
              </div>
              <TextArea
                value={drivers[i]?.riskDriver}
                onChange={(v) => patchDriver(i, v)}
                testId={`edit-driver-${i}`}
                rows={2}
                placeholder="Concise sentence with key term or % bolded automatically"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Executive Summary */}
      <div className="mb-10">
        <div className="font-heading-bold text-lg text-[#190F08] mb-4">Executive Summary</div>
        <div className="space-y-4">
          <TextArea
            value={draft.executiveSummary?.para1}
            onChange={(v) => patchSummary({ para1: v })}
            testId="edit-summary-para1"
            rows={4}
            placeholder="Paragraph 1"
          />
          <TextArea
            value={draft.executiveSummary?.para2}
            onChange={(v) => patchSummary({ para2: v })}
            testId="edit-summary-para2"
            rows={4}
            placeholder="Paragraph 2"
          />
        </div>
      </div>

      {/* Risk Categories */}
      <div className="mb-10">
        <div className="font-heading-bold text-lg text-[#190F08] mb-4">Risk Categories</div>
        <div className="space-y-6">
          {(draft.riskScores || []).map((r, i) => (
            <div
              key={r.category || i}
              className="border border-[#A88059]/40 p-5 bg-white/40"
              data-testid={`edit-risk-${i}`}
            >
              <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-4 mb-4">
                <Field label="Category">
                  <TextInput
                    value={r.category}
                    onChange={(v) => patchRiskScore(i, { category: v })}
                    testId={`edit-risk-category-${i}`}
                  />
                </Field>
                <Field label="Score">
                  <NumberInput
                    value={r.score}
                    onChange={(v) => patchRiskScore(i, { score: v })}
                    testId={`edit-risk-score-${i}`}
                  />
                </Field>
                <Field label="Tier">
                  <SingleSelect
                    testId={`edit-risk-tier-${i}`}
                    options={TIERS}
                    value={r.tier || ""}
                    onChange={(v) => patchRiskScore(i, { tier: v })}
                  />
                </Field>
              </div>
              <Field label="Narrative">
                <TextArea
                  value={r.narrative}
                  onChange={(v) => patchRiskScore(i, { narrative: v })}
                  testId={`edit-risk-narrative-${i}`}
                  rows={4}
                />
              </Field>
            </div>
          ))}
        </div>
      </div>

      {/* What to Watch */}
      <div className="mb-10">
        <div className="font-heading-bold text-lg text-[#190F08] mb-4">What to Watch</div>
        <div className="space-y-4">
          {(draft.whatToWatch || []).map((w, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
              <TextInput
                value={w.development}
                onChange={(v) => patchWatch(i, { development: v })}
                testId={`edit-watch-dev-${i}`}
                placeholder="Development"
              />
              <TextInput
                value={w.relevance}
                onChange={(v) => patchWatch(i, { relevance: v })}
                testId={`edit-watch-rel-${i}`}
                placeholder="Relevance to client"
              />
              <button
                type="button"
                onClick={() => removeWatch(i)}
                className="btn-ghost px-2 py-2 shrink-0"
                aria-label="Remove"
                data-testid={`edit-watch-remove-${i}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addWatch}
            className="btn-ghost inline-flex items-center text-xs"
            data-testid="edit-watch-add"
          >
            <Plus size={12} className="mr-1" /> Add row
          </button>
        </div>
      </div>

      {/* Aurora Recommendation — timeline groups */}
      <div className="mb-2">
        <div className="font-heading-bold text-lg text-[#190F08] mb-4">
          Aurora&apos;s Recommendation
        </div>
        <div className="space-y-6">
          <div>
            <div className="label-eyebrow mb-2">Immediate actions</div>
            <StringList
              items={draft.auroraRecommendation?.immediate || []}
              onChange={(items) => patchRec({ immediate: items })}
              testIdPrefix="edit-rec-immediate"
              placeholder="Action item"
            />
          </div>
          <div>
            <div className="label-eyebrow mb-2">3–6 month actions</div>
            <StringList
              items={draft.auroraRecommendation?.threeToSixMonths || []}
              onChange={(items) => patchRec({ threeToSixMonths: items })}
              testIdPrefix="edit-rec-mid"
              placeholder="Action item"
            />
          </div>
          <div>
            <div className="label-eyebrow mb-2">Long-term actions</div>
            <StringList
              items={draft.auroraRecommendation?.longTerm || []}
              onChange={(items) => patchRec({ longTerm: items })}
              testIdPrefix="edit-rec-long"
              placeholder="Action item"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
